// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../client';
import { getPortalRootDOMElement } from '../../DOMElements';
import { ZIndices } from '../../styled-components/dark/common';
import { Overlay } from '../Modal/Modal.style';

const ANIMATION_DURATION = 250; //ms

const appearAnimModal = keyframes`
  0% {
    bottom: -100%;
  }
  100% {
    bottom: 0.6em;
    bottom: calc(.6em + constant(safe-area-inset-bottom));
    bottom: calc(.6em + env(safe-area-inset-bottom));
  }
`;

const appearAnimModalDesktop = keyframes`
  0% {
    top: -.6em;
    opacity: 0;
  }
  100% {
    top: 3em;
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  background: transparent;
  position: absolute;
  overflow: hidden;
  user-select: none;

  ${Target.__MOBILE__
    ? css`
        bottom: 0.6em;
        bottom: calc(0.6em + constant(safe-area-inset-bottom));
        bottom: calc(0.6em + env(safe-area-inset-bottom));
        top: auto;
        left: 0.6em;
        right: 0.6em;
      `
    : css`
        bottom: auto;
        top: 3em;
        left: auto;
        right: 0.4em;
      `}

  z-index: ${ZIndices.betslipModal};

  &.menu-modal-transitions-enter {
    ${Target.__MOBILE__
      ? css`
          animation: ${appearAnimModal} ${ANIMATION_DURATION}ms ease-out forwards;
        `
      : css`
          animation: ${appearAnimModalDesktop} ${ANIMATION_DURATION}ms ease-out forwards;
        `}
  }

  &.menu-modal-transitions-exit {
    ${Target.__MOBILE__
      ? css`
          animation: ${appearAnimModal} ${ANIMATION_DURATION}ms ease-out forwards reverse;
        `
      : css`
          animation: ${appearAnimModalDesktop} ${ANIMATION_DURATION}ms ease-out forwards reverse;
        `}
  }
`;

type MenuModalProps = {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

const MenuModal = ({ className = '', isOpen, onClose, children }: MenuModalProps) => {
  const handleCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const ModalContent = useMemo(
    () => (
      <>
        {isOpen ? <Overlay onClick={handleCloseModal} $zIndex={ZIndices.betslipModal} /> : null}
        <TransitionGroup>
          {isOpen ? (
            <CSSTransition timeout={ANIMATION_DURATION} mountOnEnter unmountOnExit classNames="menu-modal-transitions">
              <Wrapper className={className}>{children}</Wrapper>
            </CSSTransition>
          ) : null}
        </TransitionGroup>
      </>
    ),
    [children, className, handleCloseModal, isOpen],
  );

  return Target.__MOBILE__ ? createPortal(ModalContent, getPortalRootDOMElement()) : ModalContent;
};

export default MenuModal;
